import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, TemplateRef } from "@angular/core";
import { Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { AdaptClientConfiguration } from "@common/configuration/adapt-client-configuration";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { IGroupedData } from "@common/lib/utilities/grouped-data.interface";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IDxComponentWithDimensionChanged } from "@common/ux/dx.types";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { IActivityEntityType, IActivityRoutes, ICalendarCulturalActivity } from "@org-common/lib/activities/activities-metadata";
import { ActivityPageService } from "@org-common/lib/activities/activity-page.service";
import { ActivityType, CulturalActivityMetadataByType, CulturalActivityTypeMetadata, CulturalActivityTypes, NonCulturalActivityType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { MeetingsService } from "@org-common/lib/meetings/meetings.service";
import { ObjectivesService } from "@org-common/lib/objectives/objectives.service";
import { LoadOptions } from "devextreme/data";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import Scheduler, { AppointmentClickEvent, AppointmentFormOpeningEvent, AppointmentRenderedEvent, AppointmentTemplateData, CellClickEvent, ContentReadyEvent, ViewType } from "devextreme/ui/scheduler";
import Toolbar, { Item as ToolbarItem } from "devextreme/ui/toolbar";
import moment from "moment";
import { Observable } from "rxjs";

interface ISelectedCellData {
    date: Date;
    activities: IGroupedData<ActivityType, IActivityEntityType>[];
}

@Component({
    selector: "adapt-activity-calendar",
    templateUrl: "./activity-calendar.component.html",
    styleUrl: "./activity-calendar.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ActivityCalendarComponent extends BaseComponent implements OnInit {
    public readonly CulturalActivityTypeMetadata = CulturalActivityTypeMetadata;
    public readonly CulturalActivityMetadataByType = CulturalActivityMetadataByType;
    public readonly ProjectLabel = AdaptClientConfiguration.AdaptProjectLabel;

    @Input({ required: true }) public activityTemplate!: TemplateRef<any>;
    @Input({ required: true }) public routes!: IActivityRoutes;
    @Input({ required: true }) public shouldUpdate$!: Observable<ActivityType[]>;

    // calendar specific
    public selectedCellData?: ISelectedCellData;
    public dataSource = new DataSource({
        store: new CustomStore({
            load: (loadOptions) => this.getActivitiesForCalendar(loadOptions),
            onLoaded: (_, loadOptions) => {
                if (this.selectedCellData) {
                    // allow the selected cell to clear if the same month is loaded
                    // TODO: this still breaks if the selected cell is not currently visible in calendar
                    //  hard case to handle as the cells activities wont be in the dataSource
                    const allowSelectionClear = moment(this.selectedCellData.date).isBetween(loadOptions.startDate, loadOptions.endDate);
                    this.updateSelectedCellData(this.selectedCellData.date, allowSelectionClear);
                }
            },
            key: "activityKey",
        }),
    });
    public calendarActivities = new Map<string, IActivityEntityType>();
    private dxScheduler?: Scheduler & IDxComponentWithDimensionChanged;

    public isFirstUpdate = true;

    public constructor(
        elementRef: ElementRef,
        public activitiesService: ActivitiesService,
        private activityPageService: ActivityPageService,
        private objectivesService: ObjectivesService,
        private meetingsService: MeetingsService,
    ) {
        super(elementRef);

        this.sizeChange$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.updateCalendarDimensions());
    }

    public get page() {
        return this.activityPageService.page;
    }

    public get schedulerDate() {
        return this.activityPageService.schedulerDate;
    }

    public set schedulerDate(date: Date) {
        this.activityPageService.schedulerDate = date;
    }

    public ngOnInit() {
        this.shouldUpdate$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.dataSource.reload());
    }

    public activityTrackBy(_index: number, activity: IActivityEntityType) {
        return ActivitiesService.getKeyForActivityType(activity);
    }

    public groupTrackBy(_index: number, section: IGroupedData<ActivityType, IActivityEntityType>) {
        return section.key;
    }

    public onCalendarLoad(event: ContentReadyEvent) {
        this.dxScheduler = event.component;

        // add today button to the toolbar
        // see: https://supportcenter.devexpress.com/ticket/details/t1148405/scheduler-create-custom-buttons-in-the-toolbar
        const toolbarElement = jQuery(event.element).get(0)?.querySelector(".dx-toolbar");
        if (toolbarElement) {
            const toolbarInstance = Toolbar.getInstance(toolbarElement);
            const items = toolbarInstance.option("items") as ToolbarItem[];
            if (items.length === 2) {
                toolbarInstance.option("items", [
                    items[0],
                    {
                        widget: "dxButton",
                        location: "before",
                        options: {
                            text: "Today",
                            onClick: () => this.dxScheduler?.option("currentDate", new Date()),
                        },
                    },
                    items[1],
                ] as ToolbarItem[]);
            }
        }

        // fix items not appearing correctly on first load due to sidebar width changes etc
        this.updateCalendarDimensions();
    }

    public onCalendarCellClick({ cellData }: Pick<CellClickEvent, "cellData">) {
        this.selectedCellData = undefined;
        this.updateSelectedCellData(cellData.startDate);
    }

    public onAppointmentRendered(event: AppointmentRenderedEvent) {
        // make completed activities transparent
        if ((event.appointmentData as ICalendarCulturalActivity).completed) {
            jQuery(event.appointmentElement).addClass("opacity-75");
        }
    }

    public onAppointmentClick(event: AppointmentClickEvent, viewType: string | ViewType) {
        if (viewType === "agenda") {
            // don't allow the agenda tooltip to show on click
            event.cancel = true;
        }

        const startDate = moment(event.appointmentData.startDate).startOf("day").toDate();
        this.selectedCellData = undefined;
        this.updateSelectedCellData(startDate);
    }

    public onAppointmentHover(event: MouseEvent, appointment: AppointmentTemplateData) {
        const target = event.target as Element | null;
        if (target) {
            this.dxScheduler?.showAppointmentTooltip(appointment.appointmentData, target);
        }
    }

    public onAppointmentLeave() {
        this.dxScheduler?.hideAppointmentTooltip();
    }

    public updateCalendarDimensions() {
        // need to call this instead of repaint, as repaint will clear the user selection
        setTimeout(() => {
            if (this.dxScheduler) {
                if (this.dxScheduler._dimensionChanged) {
                    this.dxScheduler._dimensionChanged();
                } else {
                    this.log.warn("_dimensionChanged does not exist on dxScheduler");
                }
            }
        }, 100);
    }

    // disable dx from opening the appointment editor for a calendar item
    public onAppointmentFormOpening(event: AppointmentFormOpeningEvent) {
        event.cancel = true;
    }

    private async getActivitiesForCalendar(loadOptions: LoadOptions) {
        const activitiesGroupedByType = await this.activitiesService.getActivitiesGroupedByType(CulturalActivityTypes, loadOptions.startDate!, loadOptions.endDate!);

        // flatten data for calendar
        const allActivities = ArrayUtilities.distinct(Object.values(activitiesGroupedByType)
            .flatMap(({ overdue, upcoming, allItems }) => [...overdue, ...upcoming, ...allItems])
            .filter((activity) => {
                const type = ActivitiesService.getActivityType(activity);
                return this.activitiesService.isActivityTypeVisible(CulturalActivityMetadataByType[type]);
            }));
        const activityPromises = allActivities
            .map((activity) => this.activitiesService.promiseToGetCalendarEventForActivity(activity));

        // update this a bit later so the spinner shows for longer for calendar
        setTimeout(() => this.isFirstUpdate = false, 300);

        const activities = (await Promise.all(activityPromises))
            // only include activities with a date (otherwise how do we show it on a calendar??)
            .filter((e) => !!e.startDate);

        // populate the activity map
        // otherwise we get a recursion error due to circular references (breeze entities) when DX clones the appointment
        // see: https://supportcenter.devexpress.com/ticket/details/t1278699/scheduler-maximum-call-stack-size-exceeded-when-assigning-datasource
        this.calendarActivities.clear();
        for (const activity of activities) {
            if (activity.activity) {
                const key = this.activityTrackBy(0, activity.activity) ?? "";
                this.calendarActivities.set(key, activity.activity);
                activity.activity = undefined;
                activity.activityKey = key;
            }
        }

        return activities;
    }

    private updateSelectedCellData(date: Date, allowSelectionClear = false) {
        const data: ICalendarCulturalActivity[] = this.dataSource.items();
        const rawActivities = data
            .filter((e) => moment(e.startDate).isSame(date, "date") || moment(e.endDate).isSame(date, "date"))
            .sort(SortUtilities.getSortByFieldFunction("type"))
            // get the actual activities via the key
            .map(({ activityKey }) => this.calendarActivities.get(activityKey ?? ""))
            .filter((activity) => !!activity);

        // we don't unset this.selectedCellData here as we want to keep the panel open when moving between months on calendar
        // the dataSource will lose the previous activities when moving between months, so asking for the same date can possibly not return the events again
        // both click handlers (cell & appointment) will clear the selectedCellData first.
        const activities = ArrayUtilities.groupArrayBy(rawActivities, (e) => ActivitiesService.getActivityType(e));
        if (activities.length > 0 || allowSelectionClear) {
            // group the objectives in the selected cell panel
            const objectivesGroup = activities.find(({ key }) => key === NonCulturalActivityType.Objectives);
            if (objectivesGroup) {
                const objectives = objectivesGroup.items.filter((o) => !!o && o instanceof Objective);
                objectivesGroup.items = this.objectivesService.groupObjectivesByTeam(objectives);
            }

            // group the meetings in the selected cell panel
            const meetingsGroup = activities.find(({ key }) => key === NonCulturalActivityType.Meetings);
            if (meetingsGroup) {
                const meetings = meetingsGroup.items.filter((o) => !!o && o instanceof Meeting);
                meetingsGroup.items = this.meetingsService.groupMeetingsByTeam(meetings);
            }

            this.selectedCellData = { date, activities };
        }

        // detail pane opening will cause calendar to resize
        this.updateCalendarDimensions();
    }
}
